import { extendTheme, responsiveFontSizes } from '@mui/material/styles';
import { alpha, lighten } from '@mui/material/styles';

import { COLOR_MAPPING, resolveColors } from './colorPalettes';
import { createElevationStyles, createHoverElevationStyles } from './elevationStyles';

// Elevation levels
const BASE_CARD_ELEVATION = 1;

// Resolve colors for light and dark modes
const lightColors = resolveColors(COLOR_MAPPING, 'light');
const darkColors = resolveColors(COLOR_MAPPING, 'dark');

// Default MUI shadows for light mode
const defaultShadows = [
  'none',
  '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
  '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
  '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
  '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
  '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
  '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
  '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
  '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
  '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
  '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
  '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
  '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
  '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
  '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
  '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
  '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
  '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
  '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
  '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
  '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
  '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
  '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
  '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
  '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
];

export const theme = extendTheme({
  cssVarPrefix: 'mui',
  colorSchemeSelector: 'class',
  // Default to light mode shadows
  shadows: defaultShadows as any,
  colorSchemes: {
    light: {
      palette: {
        background: {
          default: lightColors.backgroundDefault,
          paper: lightColors.backgroundPaper,
        },
        text: {
          primary: lightColors.textPrimary,
          secondary: lightColors.textSecondary,
        },
        primary: {
          main: lightColors.primary,
        },
        secondary: {
          main: lightColors.secondary,
        },
        info: {
          main: lightColors.info,
        },
        error: {
          main: lightColors.error,
        },
        warning: {
          main: lightColors.warning,
        },
        success: {
          main: lightColors.success,
        },
      },
    },
    dark: {
      palette: {
        background: {
          default: darkColors.backgroundDefault,
          paper: darkColors.backgroundPaper,
        },
        text: {
          primary: darkColors.textPrimary,
          secondary: darkColors.textSecondary,
        },
        primary: {
          main: darkColors.primary,
        },
        secondary: {
          main: darkColors.secondary,
        },
        info: {
          main: darkColors.info,
        },
        error: {
          main: darkColors.error,
        },
        warning: {
          main: darkColors.warning,
        },
        success: {
          main: darkColors.success,
        },
      },
    },
  },
  typography: {
    // Use system font stack for better performance
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    // Override default heading sizes to be more reasonable
    h1: {
      fontSize: '2rem', // 32px on mobile
      fontWeight: 500,
      lineHeight: 1.2,
      '@media (min-width:600px)': {
        fontSize: '2.5rem', // 40px on desktop
      },
    },
    h2: {
      fontSize: '1.75rem', // 28px on mobile
      fontWeight: 500,
      lineHeight: 1.3,
      '@media (min-width:600px)': {
        fontSize: '2rem', // 32px on desktop
      },
    },
    h3: {
      fontSize: '1.375rem', // 22px on mobile
      fontWeight: 500,
      lineHeight: 1.4,
      '@media (min-width:600px)': {
        fontSize: '1.5rem', // 24px on desktop
      },
    },
    h4: {
      fontSize: '1.125rem', // 18px on mobile
      fontWeight: 500,
      lineHeight: 1.5,
      '@media (min-width:600px)': {
        fontSize: '1.25rem', // 20px on desktop
      },
    },
    h5: {
      fontSize: '1rem', // 16px on mobile
      fontWeight: 500,
      lineHeight: 1.5,
      '@media (min-width:600px)': {
        fontSize: '1.125rem', // 18px on desktop
      },
    },
    h6: {
      fontSize: '0.875rem', // 14px on mobile
      fontWeight: 600,
      lineHeight: 1.5,
      '@media (min-width:600px)': {
        fontSize: '1rem', // 16px on desktop
      },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Override browser default underlines on all anchor tags
        a: {
          textDecoration: 'none',
        },
        // Global styling for inline code elements
        code: {
          backgroundColor: 'var(--mui-palette-action-hover)',
          padding: '2px 4px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '0.875em',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const elevation = ownerState?.elevation ?? 0;
          return {
            // Apply border radius to Paper components except when they're square or part of AppBar
            borderRadius: ownerState?.square ? 0 : 8,
            // Apply elevation tinting for dark mode only
            ...createElevationStyles(theme, elevation),
            // Add subtle border for better definition when elevated
            ...(elevation > 0 &&
              ownerState?.variant !== 'outlined' && {
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              }),
          };
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          borderRadius: '0 !important',
          backgroundColor: lightColors.appBarBg,
          color: lightColors.appBarFg,
          // AppBar is not a card, so no special elevation handling needed
          // Light mode will use standard shadows, dark mode uses custom colors
          // Handle dark mode
          [theme.getColorSchemeSelector('dark')]: {
            backgroundColor: darkColors.appBarBg,
            color: darkColors.appBarFg,
          },
        }),
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: BASE_CARD_ELEVATION, // Set default elevation for all cards
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const elevation = ownerState?.elevation ?? BASE_CARD_ELEVATION;
          return {
            borderRadius: 8,
            // Apply elevation tinting for dark mode
            ...createElevationStyles(theme, elevation),
            // Add subtle border for better definition
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          };
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:last-child': {
            paddingBottom: theme.spacing(2),
          },
        }),
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: ({ theme }) => {
          // Add hover elevation effect only to clickable cards
          // Get the parent card's elevation (default to BASE_CARD_ELEVATION)
          return createHoverElevationStyles(theme, BASE_CARD_ELEVATION);
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Remove motion-based hover effects
          transition: 'background-color 200ms ease-in-out, color 200ms ease-in-out',
          '&:hover': {
            // No transform or box-shadow, just color changes
            transform: 'none',
          },
        }),
        contained: ({ theme, ownerState }) => ({
          // Contained buttons start at level 2
          ...createElevationStyles(theme, 2),
          ...createHoverElevationStyles(theme, 2),
        }),
        outlined: ({ theme }) => ({
          // Outlined buttons have no elevation but still get hover effect
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.black, 0.04),
          },
        }),
        text: ({ theme }) => ({
          // Text buttons get subtle hover effect
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.black, 0.04),
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Icon buttons get subtle hover effect
          transition: 'background-color 200ms ease-in-out',
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.black, 0.04),
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          // Dialogs use level 3 elevation
          ...createElevationStyles(theme, 3),
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          // Menus use level 2 elevation
          ...createElevationStyles(theme, 2),
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          // Apply MD3 surface tinting based on elevation (if elevation is set)
          ...createElevationStyles(theme, ownerState?.elevation ?? 0),
          '&:before': {
            display: 'none', // Remove default divider
          },
        }),
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ theme }) => ({
          // FABs use level 3 elevation at rest
          ...createElevationStyles(theme, 3),
          ...createHoverElevationStyles(theme, 3),
        }),
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'none',
      },
    },
  },
});
